import { EyeIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { DocumentTextIcon } from '@heroicons/react/24/solid'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import SearchAndCreateButton from '../../components/SearchAndCreateButton'
import Spin from '../../components/Spin'
import i18n from '../../i18n'
import { apiGet } from '../../libs/apiHelper'
import { useAppContext } from '../../libs/context'
import { dateToString } from '../../libs/date'
import CreateCampaign from './Create'
import DeleteModal from './DeleteModal'
import { getFormatter } from '../../config/uploadFormatters'
import FormatterMapper from '../../components/FormatterMapper'
import MapperEditor from '../../components/MapperEditor'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const Templates = () => {
  const { campaignsTypes, setCampaignsTypes, userInfo } = useAppContext()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const search = window.location.search
  const qParams = new URLSearchParams(search)
  const templateName = qParams.get('name')
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [openDelete, setOpenDelete] = useState(false)
  const [deleteTempalte, setDeleteTemplate] = useState(null)
  const [filterText, setFilterText] = useState('') // Texto de filtro
  const [filterDebounceTimer, setFilterDebounceTimer] = useState(null) // Timer para debounce del filtro
  const [searchExpanded, setSearchExpanded] = useState(false) // Estado del buscador expandido
  const [openMapper, setOpenMapper] = useState(null) // Template con el mapper de subida desplegado
  const [editingMapper, setEditingMapper] = useState(null) // Template cuyo mapper se está editando

  // Tras guardar el mapper: refrescar la template en la lista y en el contexto
  // (la subida de contactos lee la template de ahí).
  const applySavedMapper = (name, uploadMapper) => {
    const patch = (list) =>
      (list || []).map((t) => {
        if (t.name !== name) return t
        const next = { ...t }
        if (uploadMapper) next.uploadMapper = uploadMapper
        else delete next.uploadMapper
        return next
      })
    setTemplates(patch)
    if (Array.isArray(campaignsTypes) && campaignsTypes.length) {
      setCampaignsTypes(patch(campaignsTypes))
    }
    setEditingMapper(null)
  }

  const getCampaignsTypes = async (filter = '') => {
    // Solo mostrar loading si no hay templates cargados (carga inicial)
    // NO mostrar loading cuando solo cambia el filtro para evitar que parezca un refresh
    const isInitialLoad = templates.length === 0 && !filter
    if (isInitialLoad) {
      setLoading(true)
    }

    try {
      // Usar el helper que automáticamente incluye el token de impersonación si está disponible
      const queryParams = filter ? `?filter=${encodeURIComponent(filter)}` : ''
      const req = await apiGet(`/campaigns-types${queryParams}`)
      const cleanData = req.map((item) => {
        // Manejar elements como string JSON o como objeto ya parseado
        const parseText = typeof item.elements === 'string' 
          ? JSON.parse(item.elements) 
          : item.elements
        let keys = Object.keys(parseText).find((key) => key.includes('init-1'))
        let text
        if (!keys) {
          keys = Object.keys(parseText).find((key) => key.includes('initSMS'))
          text = parseText[keys]
        } else {
          if (parseText[keys].RCSMessage) {
            text = parseText[keys].RCSMessage.textMessage
          } else if (parseText[keys].contentMessage) {
            text = parseText[keys].contentMessage.text
          } else {
            text = parseText[keys].messageText
          }
        }
        item.text = text?.length > 20 ? `${text.substr(0, 50)}...` : text
        const parseDate = dateToString(item.timestamp)
        item.date = parseDate.dayMin
        item.time = parseDate.time
        item.initElement = keys
        item.schema = item.schema ? item.schema : 'basic'
        return item
      })
      console.log(cleanData)
      if (templateName) {
        setSelectedTemplate(cleanData.find((cd) => cd.name === templateName))
      }
      setTemplates(cleanData)
    } catch (err) {
      console.log('error', err)
      setErrorMessage('There were problems loading the campaign types')
    }
    setLoading(false)
  }

  // Mantener el buscador expandido si hay filtro activo
  useEffect(() => {
    const hasActiveFilter = filterText && filterText.trim()
    if (hasActiveFilter && !searchExpanded) {
      setSearchExpanded(true)
    }
  }, [filterText, searchExpanded])

  // Efecto para manejar el filtro con debounce
  useEffect(() => {
    // Limpiar timer anterior si existe
    if (filterDebounceTimer) {
      clearTimeout(filterDebounceTimer)
    }

    // Si el filtro está vacío inmediatamente, recargar con delay corto
    // Si hay texto, esperar 400ms para evitar demasiadas llamadas mientras se escribe
    const trimmedFilter = filterText ? filterText.trim() : ''
    const delay = trimmedFilter ? 400 : 150 // Delay corto cuando está vacío para recargar rápido

    const timer = setTimeout(() => {
      const currentFilter = filterText ? filterText.trim() : ''
      console.log('[Templates] 🔍 Filtro cambiado, recargando templates...', {
        filterText: currentFilter || '(vacío - mostrar todas)',
        delay
      })

      // Llamar a getCampaignsTypes con el filtro actual (puede estar vacío)
      getCampaignsTypes(currentFilter)
    }, delay)

    setFilterDebounceTimer(timer)

    // Cleanup
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText])

  useEffect(() => {
    if (!campaignsTypes || !campaignsTypes.length) {
      getCampaignsTypes()
    } else {
      setSelectedTemplate(campaignsTypes.find((ct) => ct.name === templateName))
      setTemplates(campaignsTypes)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <div className="flex justify-center my-16">
          <Spin type="lightBg" />
        </div>
      ) : selectedTemplate ? (
        <CreateCampaign
          setSelectedTemplate={setSelectedTemplate}
          template={selectedTemplate}
          setTemplates={setTemplates}
        />
      ) : (
        <div className="space-y-4 pb-24">
          <DeleteModal
            open={openDelete}
            setOpen={setOpenDelete}
            template={deleteTempalte}
            setTemplates={setTemplates}
          />
          {templates.length === 0 && !loading ? (
            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <p className="text-lg font-medium mb-2">
                {filterText
                  ? i18n.t('no_templates_found') ||
                    'No se encontraron templates'
                  : i18n.t('no_templates') || 'No hay templates'}
              </p>
              <p className="text-sm text-gray-400">
                {filterText
                  ? 'Intenta con otro término de búsqueda'
                  : 'Los templates aparecerán aquí cuando los crees'}
              </p>
            </div>
          ) : (
            templates.map((campaign) => {
              const formatter = getFormatter(campaign)
              return (
              <div key={campaign.name}>
              <div
                value={campaign}
                className={classNames(
                  'border-gray-300 relative block bg-white border shadow-sm px-6 py-4 sm:flex sm:justify-between focus:outline-none',
                  openMapper === campaign.name ? 'rounded-t-lg' : 'rounded-lg'
                )}
              >
                <>
                  <span className="flex items-center w-full">
                    <span className="text-sm flex flex-col">
                      <div
                        as="span"
                        className="font-medium text-grey-blue flex items-center gap-2"
                      >
                        <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span>{campaign.name}</span>
                        {(formatter || userInfo.isAdmin) && (
                          <button
                            type="button"
                            onClick={() =>
                              setOpenMapper(openMapper === campaign.name ? null : campaign.name)
                            }
                            title="Ver mapper de subida (columnas del fichero → variables)"
                            className="relative z-10 inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-100"
                          >
                            Mapper: {formatter ? formatter.label : 'genérico'}
                          </button>
                        )}
                      </div>
                      <div as="span" className="text-gray-500">
                        <span className="block sm:inline">{campaign.text}</span>
                      </div>
                    </span>
                  </span>
                  <div
                    as="span"
                    className="mt-2 flex text-sm sm:mt-0 sm:flex-col sm:ml-4 sm:text-right"
                  >
                    <span className="font-medium text-grey-blue">
                      {campaign.date}
                    </span>
                    <span className="ml-1 text-gray-500 sm:ml-0">
                      {campaign.time}
                    </span>
                  </div>

                  {userInfo.isAdmin ? (
                    <>
                      <div
                        as="span"
                        className="mt-2 flex self-center text-sm sm:mt-0 sm:flex-col sm:ml-4 sm:text-right"
                      >
                        <Link to={`/templates?name=${campaign.name}`}>
                          <button
                            onClick={() => {
                              setSelectedTemplate(campaign)
                            }}
                            className="flex-shrink-0 text-blue-800 flex items-center justify-center w-10"
                          >
                            <PencilSquareIcon className="h-6 w-6 align-center" />
                          </button>
                        </Link>
                      </div>
                      <div
                        as="span"
                        className="mt-2 flex self-center text-sm sm:mt-0 sm:flex-col sm:ml-4 sm:text-right"
                      >
                        <button
                          onClick={() => {
                            setDeleteTemplate(campaign)
                            setOpenDelete(true)
                          }}
                          className="flex-shrink-0 text-blue-800 flex items-center justify-center w-10"
                        >
                          <TrashIcon className="h-6 w-6 align-center" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div
                      as="span"
                      className="mt-2 flex self-center text-sm sm:mt-0 sm:flex-col sm:ml-4 sm:text-right"
                    >
                      <Link to={`/templates?name=${campaign.name}`}>
                        <button
                          onClick={() => setSelectedTemplate(campaign)}
                          className="flex-shrink-0 text-blue-800 flex items-center justify-center w-10"
                        >
                          <EyeIcon className="h-6 w-6 align-center" />
                        </button>
                      </Link>
                    </div>
                  )}
                  <span
                    className={
                      'border-2 border-transparent absolute -inset-px rounded-lg pointer-events-none'
                    }
                    aria-hidden="true"
                  />
                </>
              </div>
              {openMapper === campaign.name && (
                <div className="border border-t-0 border-gray-300 rounded-b-lg bg-gray-50 px-6 py-4">
                  {editingMapper === campaign.name ? (
                    <MapperEditor
                      template={campaign}
                      formatter={formatter}
                      onSaved={(m) => applySavedMapper(campaign.name, m)}
                      onCancel={() => setEditingMapper(null)}
                    />
                  ) : (
                    <>
                      {formatter ? (
                        <FormatterMapper formatter={formatter} />
                      ) : (
                        <p className="text-sm text-gray-600">
                          Sin mapper propio: la subida usa el formato genérico (columna{' '}
                          <span className="font-mono">phone</span> y el resto de columnas con su
                          mismo nombre como variables).
                        </p>
                      )}
                      {userInfo.isAdmin && (
                        <div className="mt-3 flex items-center gap-3">
                          {formatter?.excel ? (
                            <p className="text-xs text-gray-500">
                              El Excel de esta template lo procesa el backend
                              (processExcelCaixarecobros); su mapper no se edita desde aquí.
                            </p>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setEditingMapper(campaign.name)}
                              className="rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
                            >
                              {formatter ? 'Editar mapper' : 'Crear mapper'}
                            </button>
                          )}
                          {formatter?.fromTemplate && (
                            <span className="text-xs text-gray-500">
                              Mapper guardado en la template
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              </div>
              )
            })
          )}

          {/* Búsqueda y crear: solo para admins */}
          {userInfo.isAdmin && (
            <SearchAndCreateButton
              filterText={filterText}
              setFilterText={setFilterText}
              createRoute="/createCampaign"
              searchPlaceholder={
                i18n.t('filter_templates') ||
                'Filtrar templates por nombre, remitente...'
              }
              createButtonTitle={i18n.t('create_template') || 'Create Template'}
              searchExpanded={searchExpanded}
              setSearchExpanded={setSearchExpanded}
            />
          )}
        </div>
      )}
    </>
  )
}

export default Templates
